'use client'

import { useSignals } from '@preact/signals-react/runtime'
import { useCountry } from '@susu/headless-commerce/contexts/country/client'
import { useHeader } from '@susu/headless-commerce/contexts/header/client'
import { useLocale } from '@susu/headless-commerce/contexts/locale/client'
import { useClientNav } from '@susu/headless-commerce/features/navigation/components/ClientNavProvider'
import { usePageType } from '@susu/headless-commerce/hooks/usePageType'
import log from '@susu/headless-commerce/utils/log'
import { disableScroll } from '@susu/headless-commerce/utils/scrollUtils'
import { URLData } from '@susu/headless-commerce/utils/tracking/segment'
import { trackEvent } from '@susu/headless-commerce/utils/tracking/tracking'
import type { ReactNode } from 'react'
import { useCallback } from 'react'
import styles from '../Header.module.scss'

export type ClientMenuButtonProps = {
  children?: ReactNode
}

export default function ClientMenuButton({
  children,
}: Readonly<ClientMenuButtonProps>) {
  useSignals()
  const pageType = usePageType()
  const country = useCountry()
  const locale = useLocale()
  const { openNav, isNavOpened } = useClientNav()
  const { menuOpen, headerTrackingValue } = useHeader()

  const handleMenuClick = useCallback(async () => {
    const { url, locationId } = URLData()

    if (isNavOpened.value === false) {
      openNav()
      disableScroll()
    }

    await trackEvent({
      ga: {
        eventCategory: 'Global_Interactions',
        eventAction: 'Opened_Menu',
        eventLabel: headerTrackingValue,
      },
      segment: {
        event: 'menuClicked',
        enrichProps: {
          locale,
          country,
        },
        properties: {
          pageType: pageType,
          eventLabel: headerTrackingValue,
          eventCategory: 'global_interactions',
          eventLocation: 'menu',
          url,
          locationId,
          layer: 0,
          menuId: 'hamburger',
          parentId: '',
        },
      },
    })
  }, [
    country,
    headerTrackingValue,
    isNavOpened.value,
    locale,
    openNav,
    pageType,
  ])

  log.debug({
    method: 'HeaderMenuButton:render',
    menuOpen,
    pageType,
    headerTrackingValue,
    locale,
  })

  return (
    <button
      type="button"
      className={styles.header__btn}
      onClick={handleMenuClick}
      data-testid="button_header_menu"
    >
      {children}
    </button>
  )
}
